import { createMessage, shopifyAddressFromGoogleComponents } from "helpers";

class Geolocation {
  location = {
    coordinates:
      JSON.parse(WYATT.Cart?.data?.attributes?.location || "{}")?.coordinates ||
      null,
    address:
      JSON.parse(WYATT.Cart?.data?.attributes?.location || "{}")?.address ||
      null,
  };
  sortedStores = null;

  constructor() {
    if (!this.location.coordinates) {
      this.updateLocationFromUser();
    } else {
      document.documentElement.dataset.wyattGeolocationStatus = "location-set";
    }

    this.sortLocations = this.sortLocations.bind(this);

    WYATT.PubSub.subscribe(
      WYATT.PubSub.events.storesFetched,
      this.sortLocations,
    );
    WYATT.PubSub.subscribe(
      WYATT.PubSub.events.userLocationChange,
      this.sortLocations,
    );
  }

  setLocation(location) {
    this.location.coordinates = location;

    document.documentElement.dataset.wyattGeolocationStatus = "location-set";

    WYATT.PubSub.publish(WYATT.PubSub.events.userLocationChange);
  }

  async updateLocationFromUser() {
    const location = await this.gettingUserLocation();

    if (!location) {
      return;
    }

    this.setLocation(location);
  }

  applyGeocodeResult(result) {
    if (!result?.geometry?.location) {
      return false;
    }

    const { location } = result.geometry;
    const coordinates = {
      lat: location.lat(),
      lng: location.lng(),
    };

    this.location = {
      coordinates,
      address: {
        formatted: result.formatted_address,
        components: shopifyAddressFromGoogleComponents(
          result.address_components,
        ),
      },
    };

    return true;
  }

  async geocode(request) {
    const geocoder = new google.maps.Geocoder();

    if (!geocoder) {
      return null;
    }

    try {
      const response = await geocoder.geocode(request);

      return response.results[0] ?? null;
    } catch {
      return null;
    }
  }

  notifyAddressNotFound() {
    const message =
      WYATT.t?.store_locator?.address_not_found ??
      "We couldn't find that address. Try selecting a suggestion or enter a full address.";

    createMessage(message, { type: "error" });
  }

  async getPlaceDetails(placeId, sessionToken) {
    const places = await google.maps.importLibrary("places");
    const service = new places.PlacesService(document.createElement("div"));

    return new Promise((resolve) => {
      service.getDetails(
        {
          placeId,
          sessionToken,
          fields: ["geometry", "formatted_address", "address_components"],
        },
        (place, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !place?.geometry?.location
          ) {
            resolve(null);
            return;
          }

          resolve(place);
        },
      );
    });
  }

  async updateLocationFromAddress(address) {
    const result = await this.geocode({
      address,
      componentRestrictions: {
        administrativeArea: "LOCALITY",
        country: "ca",
      },
    });

    if (!this.applyGeocodeResult(result)) {
      this.notifyAddressNotFound();

      return false;
    }

    this.setAddressOnCart();
    this.setLocation(this.location.coordinates);

    return true;
  }

  async updateLocationFromPlaceId(placeId, sessionToken = null) {
    const result = sessionToken
      ? await this.getPlaceDetails(placeId, sessionToken)
      : await this.geocode({ placeId });

    if (!this.applyGeocodeResult(result)) {
      this.notifyAddressNotFound();

      return false;
    }

    this.setAddressOnCart();
    this.setLocation(this.location.coordinates);

    return true;
  }

  async updateLocationFromSearchForm(form) {
    const autocomplete = form.querySelector("location-autocomplete");
    const pendingPlaceId =
      autocomplete?.takePendingPlaceSelection?.()?.placeId ?? null;
    const formData = new FormData(form);
    const formPlaceId = formData.get("place_id");
    const placeId = pendingPlaceId || formPlaceId;
    const address = formData.get("location");

    if (placeId) {
      const updated = await this.updateLocationFromPlaceId(
        String(placeId),
        autocomplete?.consumeSessionToken?.() ?? null,
      );

      if (updated) {
        autocomplete?.clearPlaceSelection?.();
      }

      return updated;
    }

    if (address) {
      return this.updateLocationFromAddress(String(address).trim());
    }

    return false;
  }

  gettingUserLocation() {
    if (!window.navigator.geolocation) {
      document.documentElement.dataset.wyattGeolocationStatus =
        "location-not-supported";
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          resolve({
            lat: latitude,
            lng: longitude,
          });
        },
        (error) => {
          switch (error.code) {
            case 1:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-denied";
              break;
            case 2:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-error";
              break;
            default:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-timeout";
              break;
          }

          resolve(false);
        },
        {
          enableHighAccuracy: true,
        },
      );
    });
  }

  sortLocations() {
    if (!this.location.coordinates || !WYATT.shopify.stores) {
      return [];
    }

    const sortedStores = WYATT.shopify.stores.toSorted(
      (locationA, locationB) => {
        const distanceA = this.getDistanceBetween(
          {
            lat: locationA.address.latitude,
            lng: locationA.address.longitude,
          },
          this.location.coordinates,
        );
        const distanceB = this.getDistanceBetween(
          {
            lat: locationB.address.latitude,
            lng: locationB.address.longitude,
          },
          this.location.coordinates,
        );

        return distanceA - distanceB;
      },
    );

    this.sortedStores = sortedStores;
    WYATT.PubSub.publish(WYATT.PubSub.events.storesSorted);

    return sortedStores;
  }

  getDistanceBetween(locationA, locationB) {
    if (!locationA || !locationB) {
      return 0;
    }

    const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

    const radiusOfEarthInMeters = 6371000;
    const φ1 = degreesToRadians(locationA.lat);
    const φ2 = degreesToRadians(locationB.lat);
    const Δφ = degreesToRadians(locationB.lat - locationA.lat);
    const Δλ = degreesToRadians(locationB.lng - locationA.lng);

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return radiusOfEarthInMeters * c;
  }

  getStoresWithinRadius(meters, storeTypes = []) {
    let stores = this.sortedStores;

    if (!this.location.coordinates || !stores) {
      return [];
    }

    if (storeTypes.length > 0) {
      stores = stores.filter((store) => storeTypes.includes(store.store_type));
    }

    return stores.filter((store) => {
      const distance = this.getDistanceBetween(
        {
          lat: store.address.latitude,
          lng: store.address.longitude,
        },
        this.location.coordinates,
      );

      return distance <= meters;
    });
  }

  getStoresWithinBounds(bounds, stores = WYATT.shopify.stores) {
    if (!bounds || !stores) {
      return [];
    }

    return stores.filter((store) => {
      const { latitude, longitude } = store.address;

      if (latitude == null || longitude == null) {
        return false;
      }

      const position = new google.maps.LatLng(latitude, longitude);

      return bounds.contains(position);
    });
  }

  deliveryAvailable(location = this.location.coordinates) {
    if (!location) {
      return false;
    }

    const stores = this.sortedStores.filter(
      (store) => store.store_type === "traditional",
    );

    return stores.some((store) => {
      const distance = this.getDistanceBetween(
        {
          lat: store.address.latitude,
          lng: store.address.longitude,
        },
        location,
      );

      return distance <= store.delivery_radius;
    });
  }

  async setAddressOnCart() {
    const result = await WYATT.Cart.update({
      attributes: {
        location: JSON.stringify(this.location),
      },
    });

    if (!result) {
      window.console.error("Failed to set location on cart");
    }
  }
}

WYATT.Geolocation = new Geolocation();

export default Geolocation;
